import { WARNING_COLORS } from '@src/shared/lib/warnings';
import { Box } from '@src/shared/ui/layout';
import {
  GutterWarningIndicator,
  WarningMessageList,
} from '@src/shared/ui/WarningIndicator';
import type { SyntheticEvent } from 'react';
import { RESOURCE_GUTTER_WIDTH } from '../renderer/theme';
import type { AbsenceWarningSummary } from '../types';

const StyleSheet = {
  gutter: {
    bottom: 0,
    cursor: 'default',
    left: 0,
    position: 'absolute',
    top: 0,
    width: `${RESOURCE_GUTTER_WIDTH}px`,
    zIndex: 1,
  },
  // Inside the table: just past the 1px frame drawn at the gutter's end.
  edge: {
    bottom: 0,
    left: `${RESOURCE_GUTTER_WIDTH + 1}px`,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: '2px',
    zIndex: 1,
  },
} as const;

// The gutter looks like it is outside the table, so a click there must not
// reach the row or group it sits beside.
function stopEvent(event: SyntheticEvent) {
  event.stopPropagation();
}

/**
 * A row's warning in the gutter left of the resource column, with its
 * coloured edge inside the table. Positioned against the nearest positioned
 * ancestor, which must be the cell (see `absenceCalendarStyles`).
 */
export default function ResourceWarningGutter({
  summary,
}: {
  summary: AbsenceWarningSummary | null | undefined;
}) {
  if (!summary?.level) {
    return null;
  }

  return (
    <>
      <Box sx={StyleSheet.gutter} onClick={stopEvent} onMouseDown={stopEvent}>
        <GutterWarningIndicator
          edge={false}
          warning={summary.level}
          message={<WarningMessageList warnings={summary.warnings} />}
          ariaLabel={summary.warnings
            .map((warning) => warning.message)
            .join('. ')}
        />
      </Box>
      <Box
        aria-hidden
        sx={StyleSheet.edge}
        style={{ backgroundColor: WARNING_COLORS[summary.level] }}
      />
    </>
  );
}
